// Unified Embedding Interface
// Provider-agnostic API for text embeddings

import { createProvider, defaultProvider } from './providers.js'
import { LoadState } from './transformers.js'

/**
 * Create embed function with httpRequest dependency
 * @param {Function} httpRequest - HTTP request function
 * @returns {Function} embed function
 *
 * @example
 * const embed = createEmbed(httpRequest)
 * const [vector] = await embed({ texts: "Hello, world!", provider: 'tei' })
 */
export function createEmbed(httpRequest) {
  const providerCache = new Map()

  const getOrCreateProvider = (providerName) => {
    if (!providerCache.has(providerName)) {
      providerCache.set(providerName, createProvider(providerName, httpRequest))
    }
    return providerCache.get(providerName)
  }

  /**
   * Generate embeddings using specified provider
   * @param {Object} params
   * @param {string|string[]} params.texts - Text(s) to embed
   * @param {string} params.provider - Provider name (tei, ollama, transformers)
   * @param {string} params.model - Model name (provider-specific)
   * @param {Object} params.config - Provider-specific config
   * @returns {Promise<number[][]>} Array of embedding vectors
   */
  return async ({ texts, provider = defaultProvider, model, config = {} }) => {
    if (!texts) {
      throw new Error('texts parameter is required')
    }

    if (typeof texts === 'string') {
      if (texts.trim().length === 0) {
        throw new Error('texts cannot be empty')
      }
    } else if (Array.isArray(texts)) {
      if (texts.length === 0) {
        throw new Error('texts array cannot be empty')
      }
      if (texts.some((t) => !t || typeof t !== 'string' || t.trim().length === 0)) {
        throw new Error('All texts must be non-empty strings')
      }
    } else {
      throw new Error('texts must be a string or array of strings')
    }

    const providerAdapter = getOrCreateProvider(provider)
    const embeddings = await providerAdapter.embed({ texts, model, config })

    console.log(`[Embedding] Generated ${embeddings.length} embeddings using ${provider}`)

    return embeddings
  }
}

/**
 * Create embedding loader with unified model loading interface
 * Works with all providers - transformers has actual loading, others are no-op
 *
 * @param {Function} httpRequest - HTTP request function (can be null for transformers)
 * @returns {Object} Loader with preload, getLoadState, isModelLoaded methods
 */
export function createEmbeddingLoader(httpRequest) {
  const providerCache = new Map()

  const getOrCreateProvider = (providerName) => {
    if (!providerCache.has(providerName)) {
      providerCache.set(providerName, createProvider(providerName, httpRequest))
    }
    return providerCache.get(providerName)
  }

  const preload = async (options = {}) => {
    const { provider = defaultProvider, model, onProgress, onStateChange } = options
    const adapter = getOrCreateProvider(provider)

    if (adapter.preload) {
      await adapter.preload({
        model,
        onProgress,
        onStateChange: onStateChange
          ? (info) => onStateChange({ ...info, provider })
          : undefined,
      })
    } else {
      onStateChange?.({ state: LoadState.LOADED, provider, model })
    }
  }

  const getLoadState = (options = {}) => {
    const { provider = defaultProvider, model } = options
    const adapter = getOrCreateProvider(provider)

    if (adapter.getLoadState) {
      return adapter.getLoadState(model)
    }

    return {
      state: LoadState.LOADED,
      progress: { status: 'ready', progress: 100 },
      error: null,
    }
  }

  const isModelLoaded = (options = {}) => {
    const { provider = defaultProvider, model } = options
    const adapter = getOrCreateProvider(provider)

    if (adapter.isModelLoaded) {
      return adapter.isModelLoaded(model)
    }

    return true
  }

  const requiresPreload = (provider = defaultProvider) => {
    return provider === 'transformers'
  }

  return { preload, getLoadState, isModelLoaded, requiresPreload }
}

/**
 * Create getDimensions function
 * @param {Function} httpRequest - HTTP request function
 * @returns {Function} getDimensions function
 */
export function createGetDimensions(httpRequest) {
  const embed = createEmbed(httpRequest)

  return async ({ provider = defaultProvider, model, config = {} }) => {
    const [testVector] = await embed({ texts: 'test', provider, model, config })
    return testVector.length
  }
}

/**
 * Create checkProvider function
 * @param {Function} httpRequest - HTTP request function
 * @returns {Function} checkProvider function
 */
export function createCheckProvider(httpRequest) {
  return async (provider, config = {}) => {
    try {
      const providerAdapter = createProvider(provider, httpRequest)

      if (providerAdapter.isAvailable) {
        return await providerAdapter.isAvailable({ config })
      }

      const embed = createEmbed(httpRequest)
      await embed({ texts: 'test', provider, config })
      return true
    } catch (error) {
      return false
    }
  }
}

export { availableProviders, defaultProvider } from './providers.js'
export { LoadState } from './transformers.js'
